import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import {
  archiveProject,
  closeProject,
  createProject,
  deleteProjectAttachment,
  getProjectDetail,
  listProjectBranchOptions,
  listProjectCompanyOptions,
  listProjectCustomerEndRoles,
  listProjectDepartmentOptions,
  listProjectUserOptions,
  uploadProjectAttachment,
  updateProject,
  getProjectSubProjects,
  listProjects,
  validateProjectCompletion,
} from '../api/projectApi'
import { appToast } from '../../../../components/AppToast'

function normalizeText(value) {
  const text = String(value || '').trim()
  return text.length ? text : undefined
}

function normalizeOptionalNumber(value) {
  if (value === '' || value === null || value === undefined) return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

function normalizeOptionsToItems(data) {
  const arr = Array.isArray(data) ? data : []
  return arr.map((option) => ({ value: String(option.id), label: option.name }))
}

function normalizeOptionalAmount(value) {
  if (value === '' || value === null || value === undefined) return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

function inclusiveDaysBetween(startDate, endDate) {
  if (!startDate) return 0
  const start = new Date(startDate)
  const end = new Date(endDate)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0
  const startMid = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  const endMid = new Date(end.getFullYear(), end.getMonth(), end.getDate())
  const diffMs = endMid.getTime() - startMid.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  return Math.max(1, diffDays + 1)
}

function deriveProjectStatus(startDate, isCompleted) {
  if (Number(isCompleted || 0) === 1) return 'COMPLETED'
  if (!startDate) return 'DRAFT'
  const start = new Date(startDate)
  if (Number.isNaN(start.getTime())) return 'DRAFT'
  const today = new Date()
  const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const startMid = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  return startMid <= todayMid ? 'IN_PROGRESS' : 'DRAFT'
}

function isValidStakeholderPhone(value) {
  const text = String(value || '').trim()
  const digits = text.replace(/\D/g, '')
  return /^[0-9+\-()\s]+$/.test(text) && digits.length === 10
}

function isValidStakeholderEmail(value) {
  const text = String(value || '').trim()
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)
}

function emptyStakeholderRow(defaultRole = '') {
  return { role: defaultRole, name: '', phoneNumbers: [''], email: '' }
}

function getCurrentUserFromStorage() {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return {}
    return JSON.parse(raw) || {}
  } catch {
    return {}
  }
}

function normalizeAttachmentItems(value) {
  if (!Array.isArray(value)) return []
  const seen = new Set()
  const normalized = []
  value.forEach((raw) => {
    let item = null
    if (typeof raw === 'string') {
      const path = String(raw || '').trim()
      if (!path) return
      const fileName = path.split('/').pop()?.split('\\').pop() || path
      item = {
        id: path,
        path,
        originalName: fileName,
        uploadedAt: null,
      }
    } else if (raw && typeof raw === 'object') {
      const path = String(raw.path || '').trim()
      if (!path) return
      const fileName =
        String(raw.originalName || raw.name || '').trim() || path.split('/').pop()?.split('\\').pop() || path
      item = {
        id: String(raw.id || path),
        path,
        originalName: fileName,
        uploadedAt: raw.uploadedAt || null,
      }
    }
    if (!item) return
    const key = String(item.path || '').toLowerCase()
    if (!key || seen.has(key)) return
    seen.add(key)
    normalized.push(item)
  })
  return normalized
}

function mergeUserOptionsById(existing, incoming) {
  const map = new Map()
  ;(Array.isArray(existing) ? existing : []).forEach((item) => {
    if (!item?.id) return
    map.set(String(item.id), item)
  })
  ;(Array.isArray(incoming) ? incoming : []).forEach((item) => {
    if (!item?.id) return
    map.set(String(item.id), item)
  })
  return Array.from(map.values())
}

function stakeholderRowState(row) {
  const role = String(row?.role || '').trim()
  const name = String(row?.name || '').trim()
  const phoneNumbers = Array.isArray(row?.phoneNumbers) ? row.phoneNumbers.map(p => String(p || '').trim()).filter(Boolean) : []
  const email = String(row?.email || '').trim()
  const hasContactValue = Boolean(name || phoneNumbers.length || email)
  const isComplete = Boolean(role && name && phoneNumbers.length && email)
  return { hasContactValue, isComplete }
}

export function useProjectEntryScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const editingProjectId = searchParams.get('id')
  const returnUrl = location.state?.returnTo || '/nerve/project/list'
  
  console.log('[useProjectEntryScreen] Render. editingProjectId:', editingProjectId, 'location.state:', location.state, 'returnUrl:', returnUrl)
  const currentUser = useMemo(() => getCurrentUserFromStorage(), [])

  const savingRef = useRef(false)
  const companyIdRef = useRef('')
  const branchIdRef = useRef('')
  const canDisableTeamToggle = useMemo(() => {
    const privileges = Array.isArray(currentUser?.privileges) ? currentUser.privileges : []
    return Boolean(currentUser?.is_admin || privileges.includes('ProjectTeamDisable'))
  }, [currentUser])

  const [form, setForm] = useState({
    name: '',
    description: '',
    company_id: '',
    branch_id: '',
    department_id: '',
    manager_id: '',
    start_date: '',
    end_date: '',
    delivery_hours: '',
    priority: 'MEDIUM',
    status: 'DRAFT',
    customer_name: '',
    billable: false,
    project_billing_mode: 'AMC',
    budget: '',
    bill_due_date: '',
    received_now: '0',
    base_received_amount: '0',
    advance_amount: '',
    is_completed: 0,
    auto_create_team: false,
    team_member_user_ids: [],
    notes: '',
    attachments: [],
    project_amc_amount: '',
    project_amc_frequency: '',
    project_amc_start_date: '',
    project_amc_end_date: '',
    project_amc_currency: 'INR',
    billing_timing: 'START_OF_PERIOD',
    billing_type: '',
    billing_rate: '',
    parent_project_id: '',
    parent_project_billing_mode: '',
  })

  const [initialLabels, setInitialLabels] = useState({
    company_name: '',
    branch_name: '',
    department_name: '',
    manager_name: '',
  })
  const [stakeholders, setStakeholders] = useState([])
  const [projectCode, setProjectCode] = useState('')
  const [parentProjectOptions, setParentProjectOptions] = useState([])
  const [profitability, setProfitability] = useState(null)
  const [subProjects, setSubProjects] = useState([])

  const [managerOptions, setManagerOptions] = useState([])
  const [stakeholderRoles, setStakeholderRoles] = useState([])

  const [snapshot, setSnapshot] = useState(null)
  const [isLoading, setIsLoading] = useState(Boolean(editingProjectId))
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false)

  // Keep refs to current company/branch so fetch callbacks always see the latest value
  useEffect(() => {
    companyIdRef.current = form.company_id
    branchIdRef.current = form.branch_id
  }, [form.company_id, form.branch_id])

  useEffect(() => {
    let active = true

    async function loadStaticOptions() {
      try {
        const [roles, projectsRes] = await Promise.all([
          listProjectCustomerEndRoles(),
          listProjects({ page: 1, page_size: 500, archive: false, fromDate: '2000-01-01', toDate: '2099-12-31' }),
        ])
        if (!active) return
        setStakeholderRoles(Array.isArray(roles) ? roles : [])
        // Unwrap paginated response; fallback to raw array for future-proofing
        const rawProjects = Array.isArray(projectsRes?.items)
          ? projectsRes.items
          : Array.isArray(projectsRes)
          ? projectsRes
          : []
        // Only top-level (non-sub) projects can be parents. Exclude self when editing.
        const eligibleParents = rawProjects.filter((p) => !p.parent_project_id)
        setParentProjectOptions(
          editingProjectId
            ? eligibleParents.filter((p) => String(p.id) !== String(editingProjectId))
            : eligibleParents,
        )
      } catch (err) {
        console.error("Failed to load static options for Project Entry Screen:", err)
        if (!active) return
        setStakeholderRoles([])
        setParentProjectOptions([])
      }
    }

    async function loadProjectIfEditing() {
      if (!editingProjectId) return
      const detail = await getProjectDetail(editingProjectId)
      if (!active) return

      const nextForm = {
        name: detail?.name || '',
        description: detail?.description || '',
        company_id: detail?.company_id ? String(detail.company_id) : '',
        branch_id: detail?.branch_id ? String(detail.branch_id) : '',
        department_id: detail?.department_id ? String(detail.department_id) : '',
        manager_id: detail?.manager_id ? String(detail.manager_id) : '',
        start_date: detail?.start_date ? String(detail.start_date).slice(0, 10) : '',
        end_date: detail?.end_date ? String(detail.end_date).slice(0, 10) : '',
        delivery_hours: (() => {
          const startDate = detail?.start_date ? String(detail.start_date).slice(0, 10) : ''
          const endDate = detail?.end_date ? String(detail.end_date).slice(0, 10) : startDate
          const suggested = inclusiveDaysBetween(startDate, endDate) * 8
          return suggested > 0 ? String(suggested) : ''
        })(),
        priority: detail?.priority || 'MEDIUM',
        status: detail?.status || 'DRAFT',
        customer_name: detail?.customer_name || '',
        billable: Boolean(detail?.billable),
        project_billing_mode: detail?.project_billing_mode || 'AMC',
        budget: detail?.budget ?? '',
        bill_due_date: detail?.bill_due_date ? String(detail.bill_due_date).slice(0, 10) : '',
        received_now: '0',
        advance_amount: detail?.advance_amount ?? '',
        base_received_amount: (() => {
          const advanceAmount = Number(detail?.advance_amount ?? 0)
          const totalReceivedAmount = Number(
            detail?.total_received_amount ?? (Number(detail?.received_amount ?? 0) + advanceAmount),
          )
          const derivedReceivedAmount = totalReceivedAmount - advanceAmount
          return String(Math.max(0, Number.isFinite(derivedReceivedAmount) ? derivedReceivedAmount : 0))
        })(),
        is_completed: Number(detail?.is_completed || 0),
        auto_create_team: detail?.auto_create_team !== undefined ? Boolean(detail.auto_create_team) : false,
        team_member_user_ids: Array.isArray(detail?.team_member_user_ids)
          ? detail.team_member_user_ids.map((id) => String(id))
          : [],
        notes: detail?.notes || '',
        attachments: normalizeAttachmentItems(detail?.attachments),
        project_amc_amount: detail?.project_amc_amount ?? '',
        project_amc_frequency: detail?.project_amc_frequency || '',
        project_amc_start_date: detail?.project_amc_start_date ? String(detail.project_amc_start_date).slice(0, 10) : '',
        project_amc_end_date: detail?.project_amc_end_date ? String(detail.project_amc_end_date).slice(0, 10) : '',
        project_amc_currency: detail?.project_amc_currency || 'INR',
        billing_timing: detail?.billing_timing || 'START_OF_PERIOD',
        billing_type: detail?.billing_type || '',

        billing_rate: detail?.billing_rate ?? '',
        parent_project_id: detail?.parent_project_id ? String(detail.parent_project_id) : '',
        parent_project_billing_mode: detail?.parent_project_billing_mode || '',
      }

      const incomingStakeholders = Array.isArray(detail?.client_side_stakeholders)
        ? detail.client_side_stakeholders
        : []

      setForm(nextForm)
      setInitialLabels({
        company_name: detail?.company_name || '',
        branch_name: detail?.branch_name || '',
        department_name: detail?.department_name || '',
        manager_name: detail?.manager_name || '',
      })
      setProjectCode(detail?.project_code || '')
      setStakeholders(
        incomingStakeholders.length
          ? incomingStakeholders.map((row) => ({
              role: row.role || '',
              name: row.name || '',
              phoneNumbers: Array.isArray(row.phone_numbers)
                ? row.phone_numbers.map(String)
                : Array.isArray(row.phoneNumbers)
                ? row.phoneNumbers.map(String)
                : row.phone
                ? [String(row.phone)]
                : [''],
              email: row.email || '',
            }))
          : [emptyStakeholderRow()],
      )
      setSnapshot({
        form: nextForm,
        stakeholders: incomingStakeholders.length
          ? incomingStakeholders.map((row) => ({
              role: row.role || '',
              name: row.name || '',
              phoneNumbers: Array.isArray(row.phone_numbers)
                ? row.phone_numbers.map(String)
                : Array.isArray(row.phoneNumbers)
                ? row.phoneNumbers.map(String)
                : row.phone
                ? [String(row.phone)]
                : [''],
              email: row.email || '',
            }))
          : [emptyStakeholderRow()],
      })

      try {
        const subProjectsList = await getProjectSubProjects(editingProjectId)
        if (active) setSubProjects(subProjectsList)
      } catch (err) {
        console.error("Failed to load sub projects", err)
      }

      // ── Auto-switch to sub-project mode when editing a sub-project ──────────
      // If the loaded project has a parent_project_id, it IS a sub-project.
      // Force ?mode=subproject so the entry screen renders the sub-project layout.
      if (detail?.parent_project_id && searchParams.get('mode') !== 'subproject') {
        const next = new URLSearchParams(searchParams)
        next.set('mode', 'subproject')
        console.log('[useProjectEntryScreen] Auto-switching mode. Preserving state:', location.state)
        navigate(`?${next.toString()}`, { replace: true, state: location.state })
      }
    }

    async function bootstrap() {
      setIsLoading(true)
      setError('')
      try {
        await Promise.all([loadStaticOptions(), loadProjectIfEditing()])
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load project configuration.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    bootstrap()
    return () => {
      active = false
    }
  }, [editingProjectId])

  useEffect(() => {
    if (editingProjectId || !parentProjectOptions.length) return
    const paramParentId = searchParams.get('parent_project_id')
    if (paramParentId && !form.parent_project_id) {
      handleParentProjectChange(paramParentId)
    }
  }, [editingProjectId, parentProjectOptions, searchParams, form.parent_project_id])

  useEffect(() => {
    if (!editingProjectId) return undefined
    const projectId = Number(editingProjectId || 0)
    if (!Number.isFinite(projectId) || projectId <= 0) return undefined
    let active = true
    async function hydrateSelectedUsers() {
      const selectedIds = [
        Number(form.manager_id || 0),
        ...(Array.isArray(form.team_member_user_ids) ? form.team_member_user_ids.map((id) => Number(id || 0)) : []),
      ]
      const normalizedIds = Array.from(
        new Set(selectedIds.filter((value) => Number.isFinite(value) && value > 0)),
      )
      if (!normalizedIds.length) return
      try {
        const selectedUsers = await listProjectUserOptions({
          page: 1,
          page_size: 200,
          user_ids: normalizedIds.join(','),
          project_id: projectId,
        })
        if (!active) return
        setManagerOptions((prev) => mergeUserOptionsById(prev, selectedUsers))
      } catch {
        if (!active) return
      }
    }
    hydrateSelectedUsers()
    return () => {
      active = false
    }
  }, [editingProjectId, form.manager_id, form.team_member_user_ids])

  // ── Dynamic fetchOptions callbacks for AppDropdown ─────────────────────────
  const fetchCompanyOptions = useCallback(async (search, page, pageSize) => {
    const data = await listProjectCompanyOptions({ search, page, page_size: pageSize })
    return { items: normalizeOptionsToItems(data), hasMore: data.length === pageSize }
  }, [])

  const fetchManagerOptions = useCallback(async (search, page, pageSize) => {
    const data = await listProjectUserOptions({ search, page, page_size: pageSize })
    if (Array.isArray(data) && data.length > 0) {
      setManagerOptions((prev) => mergeUserOptionsById(prev, data))
    }
    return { items: normalizeOptionsToItems(data), hasMore: data.length === pageSize }
  }, [])

  const fetchBranchOptions = useCallback(async (search, page, pageSize) => {
    const companyId = companyIdRef.current
    if (!companyId) return { items: [], hasMore: false }
    const data = await listProjectBranchOptions({ search, page, page_size: pageSize, company_ids: companyId })
    return { items: normalizeOptionsToItems(data), hasMore: data.length === pageSize }
  }, [])

  const fetchDepartmentOptions = useCallback(async (search, page, pageSize) => {
    const companyId = companyIdRef.current
    if (!companyId) return { items: [], hasMore: false }
    const params = { search, page, page_size: pageSize, company_ids: companyId }
    if (branchIdRef.current) params.branch_ids = branchIdRef.current
    const data = await listProjectDepartmentOptions(params)
    return { items: normalizeOptionsToItems(data), hasMore: data.length === pageSize }
  }, [])

  useEffect(() => {
    if (!stakeholders.length) {
      const defaultRole = stakeholderRoles[0] || ''
      setStakeholders([emptyStakeholderRow(defaultRole)])
    }
  }, [stakeholderRoles, stakeholders.length])

  useEffect(() => {
    setForm((prev) => {
      const nextStatus = deriveProjectStatus(prev.start_date, prev.is_completed)
      if (prev.status === nextStatus) return prev
      return { ...prev, status: nextStatus }
    })
  }, [form.start_date, form.is_completed])

  const completionPercent = useMemo(() => {
    const tracked = [
      form.name,
      form.company_id,
      form.manager_id,
      form.start_date,
      form.priority,
      form.status,
      form.customer_name,
      form.description,
      form.notes,
    ]
    const filled = tracked.filter((value) => String(value || '').trim().length > 0).length
    return Math.round((filled / tracked.length) * 100)
  }, [form])

  const validStakeholders = useMemo(
    () =>
      stakeholders
        .map((row) => ({
          role: normalizeText(row.role),
          name: normalizeText(row.name),
          phone_numbers: Array.isArray(row.phoneNumbers)
            ? row.phoneNumbers.map(normalizeText).filter(Boolean)
            : [],
          email: normalizeText(row.email),
        }))
        .filter((row) => row.role && row.name && row.phone_numbers.length > 0 && row.email),
    [stakeholders],
  )

  const hasPartialStakeholders = useMemo(
    () =>
      stakeholders.some((row) => {
        const { hasContactValue, isComplete } = stakeholderRowState(row)
        return hasContactValue && !isComplete
      }),
    [stakeholders],
  )

  async function patchForm(key, value) {
    // Guard: block toggling is_completed ON
    if (key === 'is_completed' && Number(value) === 1) {
      if (!editingProjectId) {
        appToast.error(
          'A new project cannot be marked as completed on creation. ' +
            'Create it first, complete all tasks, and have all work approved.',
        )
        return
      }
      // Edit mode: run server-side validation before allowing toggle
      try {
        const validation = await validateProjectCompletion(editingProjectId)
        if (!validation.valid) {
          const errors = Array.isArray(validation.errors) ? validation.errors : []
          errors.forEach((msg) => appToast.error(msg))
          if (!errors.length) {
            appToast.error('Project cannot be marked as completed. Please check all requirements.')
          }
          return // block the toggle
        }
      } catch {
        // Non-blocking: if the validation call fails, allow the toggle (server will re-validate on save)
      }
    }
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function handleParentProjectChange(parentId) {
    if (!parentId) {
      // Clear parent link and billing mode
      setForm((prev) => ({ ...prev, parent_project_id: '', parent_project_billing_mode: '' }))
      return
    }
    const parent = parentProjectOptions.find((p) => String(p.id) === String(parentId))
    setForm((prev) => ({
      ...prev,
      parent_project_id: parentId,
      parent_project_billing_mode: parent?.project_billing_mode || 'AMC',
      // Auto-populate common fields from parent
      company_id: parent?.company_id ? String(parent.company_id) : prev.company_id,
      branch_id: parent?.branch_id ? String(parent.branch_id) : prev.branch_id,
      department_id: parent?.department_id ? String(parent.department_id) : prev.department_id,
      customer_name: parent?.customer_name || prev.customer_name,
      manager_id: parent?.manager_id ? String(parent.manager_id) : prev.manager_id,
      start_date: parent?.start_date ? String(parent.start_date).slice(0, 10) : prev.start_date,
      end_date: parent?.end_date ? String(parent.end_date).slice(0, 10) : prev.end_date,
      // Suggest sub-project name if name is empty
      name: prev.name.trim()
        ? prev.name
        : parent?.name
        ? `${parent.name} — Sub-Project`
        : prev.name,
      // Default billing type for sub-project
      billing_type: prev.billing_type || 'FIXED_PRICE',
      billable: true,
    }))
  }

  function setErrorMessage(message) {
    setError(String(message || ''))
  }

  function handleCompanyChange(value) {
    setForm((prev) => ({
      ...prev,
      company_id: value,
      branch_id: '',
      department_id: '',
    }))
  }

  function handleBranchChange(value) {
    setForm((prev) => ({
      ...prev,
      branch_id: value,
      department_id: '',
    }))
  }

  function patchStakeholder(index, key, value) {
    setStakeholders((prev) =>
      prev.map((row, rowIndex) => (rowIndex === index ? { ...row, [key]: value } : row)),
    )
  }

  function addStakeholderRow(rowValue) {
    const defaultRole = stakeholderRoles[0] || ''
    const nextRow = rowValue
      ? {
          role: String(rowValue.role || '').trim(),
          name: String(rowValue.name || '').trim(),
          phoneNumbers: Array.isArray(rowValue.phoneNumbers)
            ? rowValue.phoneNumbers.map((p) => String(p || '').trim())
            : Array.isArray(rowValue.phone_numbers)
            ? rowValue.phone_numbers.map((p) => String(p || '').trim())
            : rowValue.phone
            ? [String(rowValue.phone || '').trim()]
            : [''],
          email: String(rowValue.email || '').trim(),
        }
      : emptyStakeholderRow(defaultRole)
    setStakeholders((prev) => [...prev, nextRow])
  }

  function removeStakeholderRow(index) {
    setStakeholders((prev) => prev.filter((_, rowIndex) => rowIndex !== index))
  }

  function buildPayload() {
    const normalizedAdvanceAmount = Number(normalizeOptionalAmount(form.advance_amount) ?? 0)
    const normalizedBaseReceivedAmount = Number(normalizeOptionalAmount(form.base_received_amount) ?? 0)
    const isTimeAndMaterialSubProject =
      Boolean(form.parent_project_id) && String(form.billing_type || '').toUpperCase() === 'TIME_AND_MATERIAL'
    const normalizedReceivedNow = isTimeAndMaterialSubProject ? 0 : Number(normalizeOptionalAmount(form.received_now) ?? 0)
    const nextReceivedAmount = Math.max(0, normalizedBaseReceivedAmount + normalizedReceivedNow)
    const nextTotalReceivedAmount = Math.max(0, normalizedAdvanceAmount + nextReceivedAmount)
    const derivedStatus = deriveProjectStatus(form.start_date, form.is_completed)

    return {
      name: normalizeText(form.name),
      description: normalizeText(form.description),
      company_id: Number(form.company_id),
      branch_id: normalizeOptionalNumber(form.branch_id),
      department_id: normalizeOptionalNumber(form.department_id),
      manager_id: normalizeOptionalNumber(form.manager_id),
      start_date: normalizeText(form.start_date),
      end_date: normalizeText(form.end_date),
      priority: normalizeText(form.priority),
      status: derivedStatus,
      customer_name: normalizeText(form.customer_name),
      billable: Boolean(form.billable),
      project_billing_mode: form.parent_project_id ? null : form.project_billing_mode || 'AMC',
      budget: normalizeOptionalAmount(form.budget),
      bill_due_date: normalizeText(form.bill_due_date),
      received_amount: nextReceivedAmount,
      advance_amount: normalizedAdvanceAmount,
      total_received_amount: nextTotalReceivedAmount,
      is_completed: Number(form.is_completed) ? 1 : 0,
      auto_create_team: Boolean(form.auto_create_team),
      team_member_user_ids: (Array.isArray(form.team_member_user_ids) ? form.team_member_user_ids : [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0),
      notes: normalizeText(form.notes),
      attachments: normalizeAttachmentItems(form.attachments).map((item) => ({
        id: item.id,
        path: item.path,
        originalName: item.originalName,
        uploadedAt: item.uploadedAt,
      })),
      client_side_stakeholders: validStakeholders,
      project_amc_amount: form.project_amc_amount !== '' ? Number(form.project_amc_amount) : null,
      project_amc_frequency: form.project_amc_frequency || null,
      project_amc_start_date: form.project_amc_start_date || null,
      project_amc_end_date: form.project_amc_end_date || null,
      project_amc_currency: form.project_amc_currency || null,
      billing_timing: form.billing_timing || 'START_OF_PERIOD',
      billing_type: form.billing_type || null,

      billing_rate: form.billing_rate !== '' ? Number(form.billing_rate) : null,
      parent_project_id: form.parent_project_id ? Number(form.parent_project_id) : null,
    }
  }

  async function handleSave() {
    if (savingRef.current) return
    savingRef.current = true
    setError('')
    setSuccess('')

    if (!form.name.trim()) {
      setError('Project name is required.')
      savingRef.current = false
      return
    }
    if (!form.company_id) {
      setError('Company is required.')
      savingRef.current = false
      return
    }
    if (!form.start_date) {
      setError('Start date is required.')
      savingRef.current = false
      return
    }
    if (form.billable && form.parent_project_id) {
      const billingType = String(form.billing_type || '').toUpperCase()
      if (!billingType) {
        setError('Billing type is required for sub-projects.')
        savingRef.current = false
        return
      }
      if (!form.bill_due_date) {
        setError('Bill due date is required for billable sub-projects.')
        savingRef.current = false
        return
      }
      if (billingType === 'FIXED_PRICE' && !(Number(form.budget || 0) > 0)) {
        setError('Fixed Budget / Billable Amount is required for fixed price sub-projects.')
        savingRef.current = false
        return
      }
      if (billingType === 'TIME_AND_MATERIAL' && !(Number(form.billing_rate || 0) > 0)) {
        setError('Hourly Billing Rate is required for time and material sub-projects.')
        savingRef.current = false
        return
      }
    }
    if (
      form.billable &&
      !form.parent_project_id &&
      String(form.project_billing_mode || 'AMC').toUpperCase() === 'PROJECT_BILLING'
    ) {
      if (!(Number(form.budget || 0) > 0)) {
        setError('Fixed Budget / Billable Amount is required for project billing.')
        savingRef.current = false
        return
      }
      if (!form.bill_due_date) {
        setError('Bill due date is required for project billing.')
        savingRef.current = false
        return
      }
    }
    if (hasPartialStakeholders) {
      setError('Each stakeholder row must include role, name, phone number(s), and email.')
      savingRef.current = false
      return
    }
    const hasInvalidStakeholder = validStakeholders.some(
      (row) => row.phone_numbers.some(phone => !isValidStakeholderPhone(phone)) || !isValidStakeholderEmail(row.email),
    )
    if (hasInvalidStakeholder) {
      setError('Stakeholder phone or email format is invalid.')
      savingRef.current = false
      return
    }

    setIsSaving(true)
    try {
      const payload = buildPayload()

      if (editingProjectId) {
        await updateProject(editingProjectId, payload)
        setSuccess('Project updated successfully.')
      } else {
        const created = await createProject(payload)
        if (created?.project_code) setProjectCode(created.project_code)
        setSuccess('Project created successfully.')
      }
      console.log('[useProjectEntryScreen] handleSave completed. Navigating to:', returnUrl, 'with state:', location.state)
      window.setTimeout(() => {
        savingRef.current = false
        setIsSaving(false)
        navigate(returnUrl)
      }, 250)
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to save project configuration.'))
      savingRef.current = false
      setIsSaving(false)
    }
  }

  async function handleArchive() {
    if (!editingProjectId) return
    if (savingRef.current) return
    setError('')
    setSuccess('')
    savingRef.current = true
    setIsSaving(true)
    try {
      await archiveProject(editingProjectId)
      setSuccess('Project archived successfully.')
      window.setTimeout(() => {
        savingRef.current = false
        setIsSaving(false)
        navigate(returnUrl)
      }, 250)
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to archive project.'))
      savingRef.current = false
      setIsSaving(false)
    }
  }

  async function handleCloseProject() {
    if (!editingProjectId) return
    if (savingRef.current) return
    setError('')
    setSuccess('')
    savingRef.current = true
    setIsSaving(true)
    try {
      await closeProject(editingProjectId)
      setSuccess('Project closed successfully.')
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to close project.'))
    } finally {
      savingRef.current = false
      setIsSaving(false)
    }
  }

  function handleDiscard() {
    navigate(returnUrl)
  }

  async function handleUploadAttachment(file) {
    if (!file) return
    if (!editingProjectId) {
      setError('Save project first, then upload attachments.')
      return
    }
    setError('')
    setIsUploadingAttachment(true)
    try {
      const result = await uploadProjectAttachment(editingProjectId, file)
      const attachments = normalizeAttachmentItems(result?.attachments)
      setForm((prev) => ({ ...prev, attachments }))
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to upload attachment.'))
    } finally {
      setIsUploadingAttachment(false)
    }
  }

  async function removeAttachment(attachmentId) {
    const key = String(attachmentId || '').trim()
    if (!key) return
    if (!editingProjectId) {
      setForm((prev) => ({
        ...prev,
        attachments: normalizeAttachmentItems(prev.attachments).filter((item) => String(item.id || '') !== key),
      }))
      return
    }
    setError('')
    try {
      const result = await deleteProjectAttachment(editingProjectId, key)
      setForm((prev) => ({ ...prev, attachments: normalizeAttachmentItems(result?.attachments) }))
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to remove attachment.'))
    }
  }

  const attachmentItems = useMemo(() => normalizeAttachmentItems(form.attachments), [form.attachments])

  return {
    state: {
      form,
      stakeholders,
      projectCode,
      managerOptions,
      stakeholderRoles,
      isLoading,
      isSaving,
      error,
      success,
      isUploadingAttachment,
      isEditMode: Boolean(editingProjectId),
      completionPercent,
      attachmentItems,
      canDisableTeamToggle,
      parentProjectOptions,
      profitability,
      subProjects,
      initialLabels,
    },
    actions: {
      patchForm,
      setErrorMessage,
      handleCompanyChange,
      handleBranchChange,
      handleParentProjectChange,
      patchStakeholder,
      addStakeholderRow,
      removeStakeholderRow,
      handleSave,
      handleArchive,
      handleCloseProject,
      handleDiscard,
      handleUploadAttachment,
      removeAttachment,
      setSubProjects,
      setProfitability,
      fetchCompanyOptions,
      fetchManagerOptions,
      fetchBranchOptions,
      fetchDepartmentOptions,
    },
  }
}
